import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { Title, useNotify } from 'react-admin';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  fetchInquiryInboxThread,
  InquiryWhatsAppMessage,
  sendInquiryInboxReply,
} from './inquiryInboxApi';
import useAuthorize from '../hooks/useAuthorize';
import { INQUIRIES_MODULE_ROLES } from '../roles/portalRoles';

const InquiryInboxThread = () => {
  useAuthorize(INQUIRIES_MODULE_ROLES);
  const notify = useNotify();
  const navigate = useNavigate();
  const { threadKey: rawThreadKey } = useParams();
  const threadKey = rawThreadKey ? decodeURIComponent(rawThreadKey) : '';
  const [messages, setMessages] = useState<InquiryWhatsAppMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [contactPhone, setContactPhone] = useState<string | null>(null);
  const [inquiryId, setInquiryId] = useState<number | null>(null);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (!threadKey) return;
    setLoading(true);
    try {
      const data = await fetchInquiryInboxThread(threadKey);
      setMessages(data.messages || []);
      const last = data.messages?.[data.messages.length - 1];
      setContactPhone(last?.contact_phone || null);
      const tagged = data.messages?.find((m) => m.inquiry_id != null);
      setInquiryId(tagged?.inquiry_id ?? null);
    } catch (error) {
      console.error(error);
      notify('Failed to load thread', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [threadKey, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSendReply = async () => {
    if (!replyBody.trim()) {
      notify('Enter a reply message', { type: 'warning' });
      return;
    }
    setSending(true);
    try {
      await sendInquiryInboxReply(threadKey, replyBody.trim());
      setReplyBody('');
      notify('Reply sent', { type: 'success' });
      await load();
    } catch (error: any) {
      console.error(error);
      notify(error.response?.data?.error || 'Failed to send reply', { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
      <Title title="Inquiry thread" />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h5">Inquiry WhatsApp thread</Typography>
          <Typography variant="body2" color="text.secondary">
            {contactPhone || threadKey}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {inquiryId ? (
            <Button component={Link} to={`/inquiries/${inquiryId}/replies`} variant="outlined">
              View parsed replies
            </Button>
          ) : null}
          <Button onClick={() => navigate('/inquiry-inbox')} variant="text">
            Back to inbox
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2, minHeight: 320, maxHeight: 480, overflowY: 'auto' }}>
        {loading ? (
          <Typography>Loading…</Typography>
        ) : messages.length === 0 ? (
          <Typography color="text.secondary">No messages in this thread.</Typography>
        ) : (
          <Stack spacing={1.5}>
            {messages.map((message) => {
              const outbound = message.direction === 'outbound';
              return (
                <Box
                  key={message.provider_message_id}
                  sx={{
                    alignSelf: outbound ? 'flex-end' : 'flex-start',
                    maxWidth: '85%',
                    bgcolor: outbound ? 'primary.light' : 'grey.100',
                    color: outbound ? 'primary.contrastText' : 'text.primary',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.body || '(no text)'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {message.sent_at ? new Date(message.sent_at).toLocaleString() : ''}
                    </Typography>
                    {message.replied_by ? (
                      <Chip size="small" label={`by ${message.replied_by}`} sx={{ height: 20 }} />
                    ) : null}
                    {message.inquiry_id ? (
                      <Chip size="small" label={`#${message.inquiry_id}`} sx={{ height: 20 }} />
                    ) : null}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Reply to seller
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Free-text WhatsApp replies work when the seller has messaged within the last 24 hours.
          Otherwise use inquiry templates from the inquiry workflow.
        </Typography>
        <TextField
          multiline
          minRows={3}
          fullWidth
          placeholder="Type your WhatsApp reply…"
          value={replyBody}
          onChange={(e) => setReplyBody(e.target.value)}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            disabled={sending || !replyBody.trim()}
            onClick={handleSendReply}
          >
            {sending ? 'Sending…' : 'Send reply'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default InquiryInboxThread;
